import { useState, useEffect, useCallback } from 'react';
import patientsApi  from '../services/patientsApi';

export const usePatients = (initialFilters = {}) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        ...filters,
      };
      const response = await patientsApi.getPatients(params);
      setPatients(response.data);
      setPagination({
        currentPage: response.meta.current_page,
        perPage: response.meta.per_page,
        total: response.meta.total,
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch patients');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.perPage]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const createPatient = async (patientData) => {
    try {
      const response = await patientsApi.createPatient(patientData);
      await fetchPatients();
      return { success: true, data: response };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message || 'Failed to create patient',
      };
    }
  };

  const updatePatient = async (id, patientData) => {
    try {
      const response = await patientsApi.updatePatient(id, patientData);
      await fetchPatients();
      return { success: true, data: response };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message || 'Failed to update patient',
      };
    }
  };

  const deletePatient = async (id) => {
    try {
      await patientsApi.deletePatient(id);
      await fetchPatients();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message || 'Failed to delete patient',
      };
    }
  };

  const updateFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const changePage = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  return {
    patients,
    loading,
    error,
    pagination,
    filters,
    updateFilters,
    changePage,
    createPatient,
    updatePatient,
    deletePatient,
    refresh: fetchPatients,
  };
};